/**
 * Header indicator for Brain updates running in the background (saving or
 * deleting a document hands its indexing to BrainIndexingProvider). A spinner
 * next to the brand while anything is running; click it to see what each update
 * is doing and for how long. Updates that just ended stay in the list for a
 * while (a failure longer), then the indicator disappears.
 */
import React, { useEffect, useState } from 'react';
import { Badge, List, Popover, Typography } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { useBrainIndexing } from '../../contexts/BrainIndexingContext';
import {
  describeJob,
  overallStatus,
  runningCount,
  type ActivityStatus,
} from '../../utils/brainIndexingActivity';

const { Text } = Typography;

const STATUS_COLOR: Record<ActivityStatus, string> = {
  running: '#1677ff',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
};

function StatusIcon({ status, size = 14 }: { status: ActivityStatus; size?: number }): React.ReactElement {
  const style = { fontSize: size, color: STATUS_COLOR[status] };
  if (status === 'running') return <LoadingOutlined spin style={style} />;
  if (status === 'success') return <CheckCircleFilled style={style} />;
  if (status === 'warning') return <ExclamationCircleFilled style={style} />;
  return <CloseCircleFilled style={style} />;
}

/** Re-renders every second while `active`, so the elapsed times keep counting. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

const BrainIndexingIndicator: React.FC<{ placement?: TooltipPlacement }> = ({ placement = 'bottomLeft' }) => {
  // The count comes from the listed jobs (not the provider's `running`
  // counter) so the badge and the list can never disagree.
  const { jobs } = useBrainIndexing();
  const inProgress = runningCount(jobs);
  const now = useNow(inProgress > 0);
  const status = overallStatus(jobs);
  if (!status) return null;

  // Newest first, so what just happened is at the top.
  const lines = [...jobs].sort((a, b) => b.startedAt - a.startedAt).map((job) => ({ job, line: describeJob(job, now) }));

  const content = (
    <div style={{ width: 340 }}>
      <List
        size="small"
        dataSource={lines}
        locale={{ emptyText: 'Sin actividad' }}
        renderItem={({ job, line }) => (
          <List.Item key={job.id} style={{ alignItems: 'flex-start', gap: 10, padding: '8px 0' }}>
            <span style={{ marginTop: 2 }}><StatusIcon status={line.status} /></span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere' }}>{line.title}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>{line.detail}</Text>
            </div>
          </List.Item>
        )}
      />
    </div>
  );

  const label = inProgress > 0
    ? `Actualizando el Brain (${inProgress})`
    : status === 'error' ? 'Una actualización del Brain ha fallado'
    : status === 'warning' ? 'Una actualización del Brain no se pudo confirmar'
    : 'Brain actualizado';

  return (
    <Popover
      trigger="click"
      placement={placement}
      title="Actividad del Brain"
      content={content}
    >
      <button
        type="button"
        aria-label={label}
        title={label}
        data-testid="brain-indexing-indicator"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, marginLeft: 8, padding: 0,
          border: 'none', borderRadius: '50%', cursor: 'pointer',
          background: status === 'running' ? 'rgba(22,119,255,0.08)' : 'transparent',
        }}
      >
        <Badge count={inProgress > 1 ? inProgress : 0} size="small" offset={[4, -2]}>
          <StatusIcon status={status} size={16} />
        </Badge>
      </button>
    </Popover>
  );
};

export default BrainIndexingIndicator;
